import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { TitleManagerService } from 'src/app/services/title-manager/title-manager.service';
import { TitleManagerModalService } from 'src/app/services/title-manager-modal/title-manager-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-title-manager',
  templateUrl: './title-manager.component.html',
  styleUrls: ['./title-manager.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class TitleManagerComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  titleManagers: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedTitleManagers: string[] = [];

  constructor(
    private _titleManagerService: TitleManagerService,
    private _titleManagerModalService: TitleManagerModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadTitleManagers();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadTitleManagers(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedTitleManagers = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._titleManagerService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.titleManagers = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.titleManagers = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading title managers:', error);
          this.titleManagers = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadTitleManagers(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadTitleManagers(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadTitleManagers();
  }

  openAddTitleManagerModal(): void {
    this._titleManagerModalService.clearSelectedTitleManager();
    const modalElement = document.getElementById('kt_modal_title_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(titleManager: any): void {
    this._titleManagerModalService.setSelectedTitleManager(titleManager);
    const modalElement = document.getElementById('kt_modal_title_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteTitleManager(titleManager: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${titleManager.title}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(titleManager);
      }
    });
  }

  private performDelete(titleManager: any): void {
    const titleManagerId = titleManager._id || titleManager.id;
    this.subscriptions.push(
      this._titleManagerService.DeleteDataById(titleManagerId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Title manager has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadTitleManagers(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete title manager.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting title manager.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onTitleManagerAdded(): void {
    console.log('Title manager added, refreshing list...');
    this.loadTitleManagers(this.currentPage, this.searchTerm, this.filters);
  }

  onTitleManagerUpdated(): void {
    console.log('Title manager updated, refreshing list...');
    this.loadTitleManagers(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(titleManagerId: string): boolean {
    return this.selectedTitleManagers.includes(titleManagerId);
  }

  isAllSelected(): boolean {
    return this.titleManagers.length > 0 && this.selectedTitleManagers.length === this.titleManagers.length;
  }

  isIndeterminate(): boolean {
    return this.selectedTitleManagers.length > 0 && this.selectedTitleManagers.length < this.titleManagers.length;
  }

  toggleSelection(titleManagerId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedTitleManagers.includes(titleManagerId)) {
        this.selectedTitleManagers.push(titleManagerId);
      }
    } else {
      this.selectedTitleManagers = this.selectedTitleManagers.filter(id => id !== titleManagerId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedTitleManagers = this.titleManagers.map(titleManager => titleManager._id);
    } else {
      this.selectedTitleManagers = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-title-manager-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-title-manager-table-toolbar="selected"]');

    if (this.selectedTitleManagers.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedTitleManagers(): void {
    if (this.selectedTitleManagers.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedTitleManagers.length} selected title manager(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedTitleManagers.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._titleManagerService.BulkDelete(this.selectedTitleManagers).subscribe({
        next: () => {
          this.loadTitleManagers();
          this.selectedTitleManagers = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected title managers have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting title managers:', error);
          Swal.fire('Error!', 'Failed to delete selected title managers.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
